import { useState, useEffect } from 'react';
import { Box, IconButton, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import EastOutlinedIcon from '@mui/icons-material/EastOutlined';
import WestOutlinedIcon from '@mui/icons-material/WestOutlined';
import useFetch from '../../hooks/useFetch';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { buildSrcSet } from '../../utils/imageSrcSet';

// Slider spans the full viewport width, capped at 1800px.
const SLIDE_SIZES = '(min-width: 1800px) 1800px, 100vw';

const SliderContainer = styled(Paper)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  maxWidth: 1800,
  height: 'min(78vh, 820px)',
  margin: '0 auto',
  borderRadius: 0,
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down('sm')]: {
    height: '62vh',
  },
}));

const SliderTrack = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '100%',
  transition: 'transform 1.1s cubic-bezier(0.22, 0.61, 0.36, 1)',
}));

const SlideImage = styled('img')({
  flex: '1 1 0',
  minWidth: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
  },
}));

const DotsContainer = styled(Box)({
  position: 'absolute',
  bottom: 28,
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 10,
  backgroundColor: 'transparent',
});

// Thin line indicators instead of dots.
const Dot = styled('button', { shouldForwardProp: (prop) => prop !== 'selected' })(({ selected }) => ({
  width: selected ? 44 : 22,
  height: 1,
  border: 'none',
  backgroundColor: '#fff',
  opacity: selected ? 1 : 0.55,
  cursor: 'pointer',
  padding: '8px 0',
  backgroundClip: 'content-box',
  transition: 'width .5s ease, opacity .5s ease',
}));

const HeroCta = styled(Link)(({ theme }) => ({
  position: 'absolute',
  left: 'clamp(24px, 5vw, 88px)',
  bottom: 'clamp(56px, 7vh, 88px)',
  padding: '15px 36px',
  border: '1px solid #fff',
  color: '#fff',
  fontFamily: 'var(--font-sans)',
  fontSize: '0.68rem',
  letterSpacing: '0.24em',
  textTransform: 'uppercase',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  backdropFilter: 'blur(2px)',
  transition: 'background-color .35s ease, color .35s ease',
  '&:hover': { backgroundColor: '#fff', color: '#1C1B19' },
  [theme.breakpoints.down('sm')]: { left: '50%', transform: 'translateX(-50%)', bottom: 56, padding: '13px 28px' },
}));

const Slider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { data, loading, error } = useFetch('/slides?populate=*');
  const slides = data || [];

  const prevSlide = () => {
    setCurrentSlide(prev => prev === 0 ? slides.length - 1 : prev - 1);
  };

  const nextSlide = () => {
    setCurrentSlide(prev => prev === slides.length - 1 ? 0 : prev + 1);
  };

  useEffect(() => {
    if (!slides.length) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [slides.length]);

  if (error) return <Box>Something went wrong loading the slider!</Box>;
  if (loading) return <SliderContainer elevation={0} />;

  return (
    <SliderContainer elevation={0}>
      <SliderTrack
        sx={{
          width: `${slides.length * 100}%`,
          transform: `translateX(-${currentSlide * (100 / slides.length)}%)`
        }}
      >
        {slides.map((slide, idx) => {
          const imgUrl = process.env.REACT_APP_UPLOAD_URL + (slide?.img?.formats?.large?.url || slide?.img?.url);
          return (
            <SlideImage
              key={slide?.id || idx}
              src={imgUrl}
              srcSet={buildSrcSet(slide?.img)}
              sizes={SLIDE_SIZES}
              alt={slide?.img?.alternativeText || `Carlo Bossi – ${idx + 1}`}
              loading="eager"
              decoding="async"
              // The first slide is the hero image: fetch it ahead of everything else.
              // Lowercase attribute because React 18 doesn't know the fetchPriority prop.
              fetchpriority={idx === 0 ? 'high' : 'auto'}
            />
          );
        })}
      </SliderTrack>

      <HeroCta to="/products">
        <FormattedMessage id="discover_collection" defaultMessage="Odkryj kolekcję" />
      </HeroCta>

      {/* Dot navigation */}
      <DotsContainer>
        {slides.map((_, idx) => (
          <Dot
            key={idx}
            selected={idx === currentSlide}
            onClick={() => setCurrentSlide(idx)}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </DotsContainer>

      {/* Optional arrow buttons if needed */}
      {/* 
      <NavigationButton sx={{ left: 10 }} onClick={prevSlide}>
        <WestOutlinedIcon />
      </NavigationButton>
      <NavigationButton sx={{ right: 10 }} onClick={nextSlide}>
        <EastOutlinedIcon />
      </NavigationButton> 
      */}
    </SliderContainer>
  );
};

export default Slider;
